import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Spinner,
    DateRangePicker,
    Input,
    Autocomplete,
    AutocompleteItem,
    Tabs,
    Tab,
} from "@heroui/react";
import { LuSearch, LuPrinter } from "react-icons/lu";
import { parseDate } from "@internationalized/date";
import { useFetchData, useDebounce } from "@/hooks";
import { PageHeader, ThemedText, ErrorState, EmptyState } from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";

const ReceivableReport = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedTab = searchParams.get("tab") || "all";
    const todayStr = new Date().toISOString().split("T")[0];
    const customRange = {
        start: parseDate(searchParams.get("start") || todayStr),
        end: parseDate(searchParams.get("end") || todayStr),
    };
    const selectedCustomerId = searchParams.get("customer_id") || "";

    const [searchInput, setSearchInput] = useState(searchParams.get("order_number") || "");
    const debouncedSearch = useDebounce(searchInput, 500);

    useEffect(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (debouncedSearch) next.set("order_number", debouncedSearch);
            else next.delete("order_number");
            return next;
        });
    }, [debouncedSearch]);

    const updateParams = (updates) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v) next.set(k, v);
                else next.delete(k);
            });
            return next;
        });
    };

    const { data: customers } = useFetchData("select/customer");

    const getReportParams = () => {
        const base = {
            customer_id: selectedCustomerId || undefined,
            order_number: debouncedSearch || undefined,
        };
        if (selectedTab === "custom") {
            return {
                ...base,
                date_from: customRange.start.toString(),
                date_to: customRange.end.toString(),
            };
        }
        return base;
    };

    const { data, isLoading, error, refetch } = useFetchData(
        "report/receivable",
        getReportParams(),
    );

    const orders = data?.orders || [];
    const summary = data?.summary || {};

    if (error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Laporan"
                    description="Terjadi kesalahan saat memuat data laporan piutang."
                    onAction={refetch}
                />
            </div>
        );
    }

    const isOverdue = (dueDate) => {
        if (!dueDate) return false;
        return new Date(dueDate) < new Date();
    };

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex items-center justify-between">
                <PageHeader
                    title="Laporan Piutang"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Laporan" },
                        { label: "Piutang" },
                    ]}
                />
                <Button
                    color="default"
                    size="sm"
                    startContent={<LuPrinter />}
                    isDisabled={isLoading || orders.length === 0}
                    onPress={() => {
                        const p = getReportParams();
                        const qs = new URLSearchParams();
                        if (p.date_from) qs.set("date_from", p.date_from);
                        if (p.date_to) qs.set("date_to", p.date_to);
                        if (p.customer_id) qs.set("customer_id", p.customer_id);
                        if (p.search) qs.set("search", p.search);
                        navigate(`/receivable-report/print?${qs.toString()}`);
                    }}
                >
                    Print
                </Button>
            </div>

            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <Tabs
                        aria-label="Date Filter"
                        selectedKey={selectedTab}
                        onSelectionChange={(key) => updateParams({ tab: key })}
                        variant="solid"
                        size="sm"
                    >
                        <Tab key="all" title="Semua" />
                        <Tab key="custom" title="Custom Range" />
                    </Tabs>

                    {selectedTab === "custom" && (
                        <DateRangePicker
                            aria-label="Pilih Periode"
                            className="max-w-xs"
                            variant="flat"
                            size="sm"
                            value={customRange}
                            onChange={(range) =>
                                updateParams({
                                    start: range.start.toString(),
                                    end: range.end.toString(),
                                })
                            }
                        />
                    )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Input
                        aria-label="Cari"
                        placeholder="Cari no. order..."
                        variant="flat"
                        size="sm"
                        className="max-w-56"
                        startContent={<LuSearch className="text-default-400" />}
                        value={searchInput}
                        onValueChange={setSearchInput}
                        isClearable
                        onClear={() => {
                            setSearchInput("");
                            updateParams({ search: "" });
                        }}
                    />
                    <Autocomplete
                        aria-label="Filter Customer"
                        placeholder="Semua Customer"
                        variant="flat"
                        size="sm"
                        className="max-w-56"
                        selectedKey={selectedCustomerId || null}
                        onSelectionChange={(key) => updateParams({ customer_id: key || "" })}
                        isClearable
                        onClear={() => updateParams({ customer_id: "" })}
                    >
                        {(customers || []).map((c) => (
                            <AutocompleteItem key={c.value}>{c.title}</AutocompleteItem>
                        ))}
                    </Autocomplete>
                </div>
            </div>

            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-row items-start justify-between px-4 pt-4 pb-2">
                    <div className="flex flex-col gap-0.5">
                        <ThemedText size="md" className="font-semibold">
                            Daftar Piutang
                        </ThemedText>
                        {!isLoading && (
                            <ThemedText size="xs" variant="caption">
                                {orders.length} sales order belum lunas
                            </ThemedText>
                        )}
                    </div>
                    {selectedTab === "custom" && (
                        <ThemedText size="xs" variant="caption">
                            Periode: {formatDate(customRange.start.toString())} s/d {formatDate(customRange.end.toString())}
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="max-h-[70vh] overflow-auto px-4 pt-2 pb-4">
                    {isLoading ? (
                        <div className="flex h-48 w-full items-center justify-center">
                            <Spinner label="Memuat data..." color="primary" />
                        </div>
                    ) : orders.length === 0 ? (
                        <EmptyState
                            title="Tidak Ada Data"
                            description="Belum ada data piutang untuk filter ini."
                        />
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>No. Order</th>
                                    <th>Customer</th>
                                    <th>Tgl. Order</th>
                                    <th>Jatuh Tempo</th>
                                    <th>Status Pembayaran</th>
                                    <th>Total</th>
                                    <th>Terbayar</th>
                                    <th>Sisa</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((row, index) => (
                                    <tr key={row.id} className="hover:bg-default-50 transition-colors">
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {index + 1}.
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <Link
                                                to={`/sales-order/${row.id}/detail`}
                                                className="text-xs font-medium text-primary hover:underline"
                                            >
                                                {row.order_number}
                                            </Link>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {row.customer || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatDate(row.order_date, "dd MMM yyyy")}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            {row.due_date ? (
                                                <ThemedText
                                                    size="xs"
                                                    className={isOverdue(row.due_date) ? "text-danger font-medium" : ""}
                                                >
                                                    {formatDate(row.due_date, "dd MMM yyyy")}
                                                </ThemedText>
                                            ) : (
                                                <ThemedText size="xs" variant="secondary">-</ThemedText>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {row.payment_status?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatCurrency(row.total_amount)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs" className="text-success">
                                                {row.total_paid > 0 || row.total_discount > 0
                                                    ? formatCurrency(row.total_paid + row.total_discount)
                                                    : "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText
                                                size="xs"
                                                className={`font-medium ${row.remaining < 0 ? "text-warning" : "text-danger"}`}
                                            >
                                                {formatCurrency(Math.abs(row.remaining))}
                                                {row.remaining < 0 ? " (lebih)" : ""}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            {orders.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td colSpan={6} className="text-center">
                                            <ThemedText size="xs" className="font-bold">TOTAL</ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs" className="font-bold">
                                                {formatCurrency(summary.total_amount)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs" className="font-bold text-success">
                                                {formatCurrency(summary.total_paid + summary.total_discount)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs" className="font-bold text-danger">
                                                {formatCurrency(summary.total_remaining)}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default ReceivableReport;
